/**
 * The SectionTypes offered when authoring a Notice-family form's Agent App review cards. Notices have no
 * tenant-signing flow (see the Notices planning doc), so the tenant-signature-specific types are excluded;
 * everything a notice genuinely needs stays available — property address, the PM's own section, the
 * recipient/representee sections (a notice needs one to know who to email), the PM's own signature at send
 * time, and plain attachments/links.
 *
 * Excluded: RecipientSignature, RepresentativeSignature (tenant-signing concepts with no notice equivalent),
 * PaymentMethod, SpecialCondition, SpecialConditionTemplate, AdditionalClause, StandardTerm, Repairers,
 * RepairerPlumbing, RepairerElectrical, RepairerOther (agreement-specific terms/repairer concepts).
 *
 * Consumed the same way `TYPED_RECIPIENT_SECTION_TYPES` is for the recipient flow: filtering the choices
 * offered in the section-type picker, with a re-admit escape hatch so a section already saved with an
 * excluded type (e.g. authored before this filter existed, or before the family was finalised) still shows
 * its real value instead of being misrepresented.
 */
export const NOTICE_SECTION_TYPES = new Set([
  'PropertyAddress',
  'PersonUser',
  'PersonRecipient',
  'PersonRepresentee',
  'Signature',
  'Document',
  'Link',
])

/**
 * Builds the Section Type options for the picker: the explicit "None – dynamic fields" sentinel first,
 * then — for a Notice — only the types that apply (see NOTICE_SECTION_TYPES above); every other family (or
 * unset) gets the full catalog. A re-admit escape hatch keeps `currentValue` visible even when it's an
 * excluded type (from before this filter existed, or from before the form's family was finalised), so
 * editing shows the real saved value instead of silently misrepresenting it as unset.
 * Pure — takes the catalog and current value as plain arguments so it's testable without rendering React.
 */
export const buildSectionTypeOptions = <T extends { value: string }>(
  catalog: T[],
  family: string | undefined,
  currentValue: string | null | undefined,
  noneOption: T,
): T[] => {
  if (family !== 'Notice') return [noneOption, ...catalog]

  const allowed = catalog.filter((option) => NOTICE_SECTION_TYPES.has(option.value))
  const currentExcluded =
    currentValue && currentValue !== 'None' && !allowed.some((option) => option.value === currentValue)
      ? catalog.find((option) => option.value === currentValue)
      : undefined
  return [noneOption, ...allowed, ...(currentExcluded ? [currentExcluded] : [])]
}
